/**
 * Linha do tempo no padrão da Linha do tempo de Requisições de Compra:
 * agrupada por dia (mais recente primeiro), hora, ícone por tipo, a frase
 * pronta do backend e o tipo em etiqueta. `ListaTimeline` desenha os itens
 * (dados/timeline.js); `TimelineSetor` busca o feed do setor e põe os
 * chips de tipo por cima.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheckSquare,
  FiClock,
  FiFileText,
  FiMessageSquare,
  FiMove,
  FiRefreshCw,
  FiScissors,
  FiUsers,
} from "react-icons/fi";

import { useAuth } from "../../../context/AuthContext";
import { listarEventosSetor } from "../../../services/mapaProducao";
import { plural } from "../../../utils/dinheiro";
import { agruparPorDia } from "../dados/timeline";
import { CabecaTimeline, CartaoTimeline, Chip, ColunaDia, Dia, Evento, IconeEvento, Tag, Vazio } from "./styles";
import { CATEGORIAS_TIMELINE, contarPorCategoria, timelineDoSetor } from "./visoes";

const LIMITE_EVENTOS = 200;

const ICONE_POR_CATEGORIA = {
  planilha: FiFileText,
  alocacao: FiMove,
  status: FiRefreshCw,
  prazo: FiClock,
  comentario: FiMessageSquare,
  tarefa: FiCheckSquare,
  peca: FiScissors,
  cadastro: FiUsers,
};

function hora(item) {
  if (item.diaInteiro) return "";
  return item.quando.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function linhaPrincipal(item) {
  // Evento do app já vem com a frase pronta ("Carlos iniciou a tarefa TF-12").
  return item.origem === "app" ? item.texto || item.titulo : item.titulo;
}

function linhaSecundaria(item) {
  if (item.origem === "planilha") return item.texto;
  return item.dados?.rotulo || "";
}

/**
 * Props: itens (timeline.js), onAbrir(item) (sem = só leitura),
 * podeAbrir(item), compacto (painel lateral), hoje, vazio.
 */
export function ListaTimeline({ itens, onAbrir, podeAbrir, compacto = false, hoje = new Date(), vazio }) {
  const dias = useMemo(() => agruparPorDia(itens, { hoje }), [itens, hoje]);
  if (dias.length === 0) return <Vazio>{vazio || "Nada registrado ainda."}</Vazio>;
  return dias.map((dia) => (
    <Dia key={dia.chave} data-compacto={compacto ? "sim" : undefined}>
      <ColunaDia $hoje={dia.hoje}>
        <div className="dds">{dia.hoje ? "Hoje" : dia.data.toLocaleDateString("pt-BR", { weekday: "long" })}</div>
        <div className="dia">{dia.data.getDate()}</div>
        <div className="mes">
          {dia.data.toLocaleDateString("pt-BR", { month: "long" })} · {plural(dia.itens.length, "evento", "eventos")}
        </div>
      </ColunaDia>
      <div style={{ minWidth: 0 }}>
        {dia.itens.map((item) => {
          const Icone = ICONE_POR_CATEGORIA[item.categoria] || FiFileText;
          const clicavel = Boolean(onAbrir) && (!podeAbrir || podeAbrir(item));
          return (
            <Evento
              key={item.id}
              type="button"
              disabled={!clicavel}
              onClick={clicavel ? () => onAbrir(item) : undefined}
            >
              <span className="hora">{hora(item)}</span>
              <IconeEvento $tom={item.tom}>
                <Icone size={14} aria-hidden="true" />
              </IconeEvento>
              <span className="texto">
                <span className="l1">{linhaPrincipal(item)}</span>
                {linhaSecundaria(item) && <span className="l2">{linhaSecundaria(item)}</span>}
              </span>
              <Tag className="tag">{item.origem === "planilha" ? "Planilha" : item.titulo}</Tag>
            </Evento>
          );
        })}
      </div>
    </Dia>
  ));
}

function useEventosDoSetor(setorId, atualizacao) {
  const { request } = useAuth();
  const [situacao, setSituacao] = useState({ eventos: [], carregando: true, erro: null });
  useEffect(() => {
    let vivo = true;
    setSituacao((s) => ({ ...s, carregando: true }));
    Promise.resolve()
      .then(() => listarEventosSetor(request, { setor_id: setorId, limite: LIMITE_EVENTOS }))
      .then((lista) => {
        if (vivo) setSituacao({ eventos: Array.isArray(lista) ? lista : [], carregando: false, erro: null });
      })
      .catch((e) => {
        if (vivo) setSituacao((s) => ({ ...s, carregando: false, erro: e?.message || "Não foi possível ler a linha do tempo." }));
      });
    return () => {
      vivo = false;
    };
    // `atualizacao` (o estado do app) muda quando alguém aponta algo: relê o feed.
  }, [request, setorId, atualizacao]);
  return situacao;
}

const CATEGORIA_CADASTRO = "cadastro";

/**
 * Feed do setor. Props: setorId, entidade, demandas/tarefas/pecas (já
 * filtradas), filtrando, atualizacao, hoje, onAbrir(item), podeAbrir(item).
 */
export default function TimelineSetor({
  setorId,
  entidade,
  demandas,
  tarefas,
  pecas,
  filtrando,
  atualizacao,
  hoje,
  onAbrir,
  podeAbrir,
}) {
  const { eventos, carregando, erro } = useEventosDoSetor(setorId, atualizacao);
  const [categoria, setCategoria] = useState(null);
  const itens = useMemo(
    () => timelineDoSetor({ eventos, demandas, tarefas, pecas, entidade, filtrando }),
    [eventos, demandas, tarefas, pecas, entidade, filtrando]
  );
  const contagem = useMemo(() => contarPorCategoria(itens), [itens]);
  // "Todos" é o que aconteceu com o TRABALHO. Cadastro (setor criado, desenho
  // alterado) tem o chip próprio: misturado, ele enterrava a produção — num
  // setor recém-organizado eram 36 de 61 registros.
  const daProducao = useMemo(() => itens.filter((i) => i.categoria !== CATEGORIA_CADASTRO), [itens]);
  const visiveis = categoria ? itens.filter((i) => i.categoria === categoria) : daProducao;

  return (
    <CartaoTimeline>
      <CabecaTimeline>
        <Chip type="button" $ativo={!categoria} aria-pressed={!categoria} onClick={() => setCategoria(null)}>
          Todos <span className="n">{daProducao.length}</span>
        </Chip>
        {CATEGORIAS_TIMELINE.filter((c) => contagem[c.valor]).map((c) => (
          <Chip
            key={c.valor}
            type="button"
            $ativo={categoria === c.valor}
            $tom={c.tom}
            aria-pressed={categoria === c.valor}
            onClick={() => setCategoria(categoria === c.valor ? null : c.valor)}
          >
            <span className="ponto" /> {c.rotulo} <span className="n">{contagem[c.valor]}</span>
          </Chip>
        ))}
        <span className="info">
          {erro
            ? erro
            : `Últimos ${LIMITE_EVENTOS} registros do app + chegadas pela planilha${carregando ? " · atualizando…" : ""}`}
        </span>
      </CabecaTimeline>
      <ListaTimeline
        itens={visiveis}
        hoje={hoje}
        onAbrir={onAbrir}
        podeAbrir={podeAbrir}
        vazio={carregando ? "Carregando a linha do tempo…" : "Nada registrado neste setor ainda."}
      />
    </CartaoTimeline>
  );
}
